import React, { useMemo, useState } from 'react';
import { buildStreetViewUrl, MapPin, useRealSignalMap } from '@/pages/realsignal/mapHooks';
import type { DealCriteria } from '../../../../schemas/realsignal/dealCriteria';

const MapHeader: React.FC = () => {
    return (
        <div className="border-b border-slate-800 px-6 py-4 flex flex-wrap items-center gap-4 justify-between">
            <div>
                <h1 className="text-2xl font-semibold">RealSignal Harris Map</h1>
                <p className="text-sm text-slate-400">
                    GIS-driven foreclosure pins (default: high confidence only)
                </p>
            </div>
        </div>
    );
};

interface MapOverlaysProps {
    mapReady: boolean;
    loading: boolean;
    error: string | null;
    pinCount: number;
}

const MapOverlays: React.FC<MapOverlaysProps> = ({ mapReady, loading, error, pinCount }) => {
    return (
        <>
            {!mapReady && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                    Loading map...
                </div>
            )}
            {loading && (
                <div className="absolute right-4 top-4 rounded bg-slate-900/80 px-3 py-1 text-xs text-slate-200">
                    Refreshing pins...
                </div>
            )}
            {error && (
                <div className="absolute left-4 top-4 max-w-sm rounded border border-red-500 bg-red-950/90 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}
            <div className="absolute left-4 bottom-4 rounded bg-slate-900/80 px-3 py-1 text-xs text-slate-200">
                Pins in view: {pinCount}
            </div>
        </>
    );
};

interface FilterPanelProps {
    minOpeningBid: string;
    maxOpeningBid: string;
    auctionWithinDays: string;
    locationConfidence: 'high' | 'any';
    hasComps: boolean;
    onMinOpeningBidChange: (value: string) => void;
    onMaxOpeningBidChange: (value: string) => void;
    onAuctionWithinDaysChange: (value: string) => void;
    onLocationConfidenceChange: (value: 'high' | 'any') => void;
    onHasCompsChange: (value: boolean) => void;
    onApply: () => void;
    onReset: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
    minOpeningBid,
    maxOpeningBid,
    auctionWithinDays,
    locationConfidence,
    hasComps,
    onMinOpeningBidChange,
    onMaxOpeningBidChange,
    onAuctionWithinDaysChange,
    onLocationConfidenceChange,
    onHasCompsChange,
    onApply,
    onReset,
}) => {
    return (
        <div className="space-y-4 rounded border border-slate-800 bg-slate-900/40 p-4">
            <div>
                <div className="text-xs uppercase tracking-wide text-slate-400">Filters</div>
                <div className="text-sm text-slate-300">Apply deal criteria (no underwriting).</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
                <label className="flex flex-col gap-1 text-slate-300">
                    Min opening bid
                    <input
                        type="number"
                        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100"
                        value={minOpeningBid}
                        onChange={event => onMinOpeningBidChange(event.target.value)}
                    />
                </label>
                <label className="flex flex-col gap-1 text-slate-300">
                    Max opening bid
                    <input
                        type="number"
                        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100"
                        value={maxOpeningBid}
                        onChange={event => onMaxOpeningBidChange(event.target.value)}
                    />
                </label>
                <label className="flex flex-col gap-1 text-slate-300 col-span-2">
                    Auction within days
                    <select
                        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100"
                        value={auctionWithinDays}
                        onChange={event => onAuctionWithinDaysChange(event.target.value)}
                    >
                        <option value="">Any</option>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="21">21 days</option>
                        <option value="30">30 days</option>
                        <option value="60">60 days</option>
                    </select>
                </label>
                <label className="flex flex-col gap-1 text-slate-300 col-span-2">
                    Location confidence
                    <select
                        className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-100"
                        value={locationConfidence}
                        onChange={event =>
                            onLocationConfidenceChange(
                                event.target.value === 'any' ? 'any' : 'high'
                            )
                        }
                    >
                        <option value="high">High only</option>
                        <option value="any">Any</option>
                    </select>
                </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
                <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-500 bg-slate-900"
                    checked={hasComps}
                    onChange={event => onHasCompsChange(event.target.checked)}
                />
                Has comps
            </label>
            <div className="flex gap-2">
                <button
                    type="button"
                    className="rounded bg-emerald-600 px-3 py-1 text-sm font-semibold text-slate-950"
                    onClick={onApply}
                >
                    Apply
                </button>
                <button
                    type="button"
                    className="rounded border border-slate-700 px-3 py-1 text-sm text-slate-200"
                    onClick={onReset}
                >
                    Reset
                </button>
            </div>
        </div>
    );
};

interface PinListProps {
    pins: MapPin[];
    selectedPinId?: string;
    onSelect: (pin: MapPin) => void;
}

const PinList: React.FC<PinListProps> = ({ pins, selectedPinId, onSelect }) => {
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const toggle = (propertyId: string) => {
        setExpanded(current => ({ ...current, [propertyId]: !current[propertyId] }));
    };

    return (
        <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-slate-400">Pins</div>
            {pins.length === 0 ? (
                <div className="text-sm text-slate-400">No pins in view.</div>
            ) : (
                <div className="space-y-2">
                    {pins.map(pin => {
                        const isSelected = pin.propertyId === selectedPinId;
                        const isExpanded = expanded[pin.propertyId] ?? false;
                        return (
                            <div
                                key={pin.propertyId}
                                className={`rounded border p-3 ${
                                    isSelected
                                        ? 'border-emerald-500 bg-slate-900/60'
                                        : 'border-slate-800 bg-slate-950/40'
                                }`}
                            >
                                <button
                                    type="button"
                                    className="w-full text-left"
                                    onClick={() => onSelect(pin)}
                                >
                                    <div className="text-sm font-semibold text-slate-100">
                                        {pin.propertyId}
                                    </div>
                                    <div className="text-xs text-slate-400">
                                        {pin.status ?? 'Unknown'} • {pin.auction_date ?? 'TBD'}
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    className="mt-2 text-xs text-slate-300 underline"
                                    onClick={() => toggle(pin.propertyId)}
                                >
                                    Why this appears
                                </button>
                                {isExpanded && (
                                    <div className="mt-2 space-y-1 text-xs text-slate-200">
                                        {pin.criteriaMatches.length === 0 ? (
                                            <div>No filters applied.</div>
                                        ) : (
                                            pin.criteriaMatches.map(match => (
                                                <div
                                                    key={`${pin.propertyId}-${match}`}
                                                    className="rounded bg-slate-900/70 px-2 py-1"
                                                >
                                                    {match}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

interface DetailMetaProps {
    selectedPin: MapPin;
}

const DetailMeta: React.FC<DetailMetaProps> = ({ selectedPin }) => {
    return (
        <div className="space-y-4">
            <div>
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Property Detail</h2>
                    {selectedPin.location_confidence === 'low' && (
                        <span className="rounded bg-amber-600/20 px-2 py-0.5 text-xs text-amber-200">
                            Approximate
                        </span>
                    )}
                </div>
                <p className="text-sm text-slate-400 break-all">{selectedPin.propertyId}</p>
            </div>
            <div className="space-y-2 text-sm">
                <div className="flex justify-between text-slate-300">
                    <span>Status</span>
                    <span className="text-slate-100">{selectedPin.status ?? 'Unknown'}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                    <span>Auction date</span>
                    <span className="text-slate-100">{selectedPin.auction_date ?? 'TBD'}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                    <span>Opening bid</span>
                    <span className="text-slate-100">
                        {selectedPin.opening_bid !== undefined
                            ? `$${selectedPin.opening_bid.toLocaleString()}`
                            : 'TBD'}
                    </span>
                </div>
            </div>
        </div>
    );
};

interface StreetViewPanelProps {
    streetViewUrl: string;
}

const StreetViewPanel: React.FC<StreetViewPanelProps> = ({ streetViewUrl }) => {
    return (
        <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-slate-400">Street View</div>
            <div className="aspect-video w-full overflow-hidden rounded border border-slate-800">
                <iframe title="Street View" src={streetViewUrl} className="h-full w-full" />
            </div>
        </div>
    );
};

interface DetailPanelProps {
    selectedPin: MapPin | null;
    streetViewUrl: string | null;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ selectedPin, streetViewUrl }) => {
    if (!selectedPin) {
        return <div className="text-sm text-slate-400">Select a pin to view details.</div>;
    }

    const matches = selectedPin.criteriaMatches ?? [];

    return (
        <div className="space-y-4">
            <DetailMeta selectedPin={selectedPin} />
            {streetViewUrl && <StreetViewPanel streetViewUrl={streetViewUrl} />}
            <div className="space-y-2">
                <div className="text-xs uppercase tracking-wide text-slate-400">
                    Why this appears
                </div>
                {matches.length === 0 ? (
                    <div className="text-sm text-slate-300">No filters applied.</div>
                ) : (
                    <ul className="space-y-1 text-sm text-slate-200">
                        {matches.map(match => (
                            <li key={match} className="rounded bg-slate-900/60 px-2 py-1">
                                {match}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

const RealSignalMapPage: React.FC = () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    const [minOpeningBid, setMinOpeningBid] = useState('');
    const [maxOpeningBid, setMaxOpeningBid] = useState('');
    const [auctionWithinDays, setAuctionWithinDays] = useState('');
    const [locationConfidence, setLocationConfidence] = useState<'high' | 'any'>('high');
    const [hasComps, setHasComps] = useState(false);
    const [appliedCriteria, setAppliedCriteria] = useState<DealCriteria>({
        locationConfidence: 'high',
    });

    const includeApproximate = appliedCriteria.locationConfidence === 'any';
    const criteriaForRequest = useMemo(() => appliedCriteria, [appliedCriteria]);

    const { mapContainerRef, pins, selectedPin, setSelectedPin, loading, error, mapReady } =
        useRealSignalMap(apiKey, criteriaForRequest, includeApproximate);

    const streetViewUrl =
        selectedPin && buildStreetViewUrl(apiKey, selectedPin.lat, selectedPin.lon);

    const applyFilters = () => {
        const criteria: DealCriteria = {
            locationConfidence,
        };
        const minBid = minOpeningBid.trim();
        if (minBid) {
            const parsed = Number(minBid);
            if (Number.isFinite(parsed)) {
                criteria.minOpeningBid = parsed;
            }
        }
        const maxBid = maxOpeningBid.trim();
        if (maxBid) {
            const parsed = Number(maxBid);
            if (Number.isFinite(parsed)) {
                criteria.maxOpeningBid = parsed;
            }
        }
        if (auctionWithinDays) {
            const parsed = Number(auctionWithinDays);
            if (Number.isFinite(parsed)) {
                criteria.auctionWithinDays = parsed;
            }
        }
        if (hasComps) {
            criteria.hasComps = true;
        }
        setAppliedCriteria(criteria);
    };

    const resetFilters = () => {
        setMinOpeningBid('');
        setMaxOpeningBid('');
        setAuctionWithinDays('');
        setLocationConfidence('high');
        setHasComps(false);
        setAppliedCriteria({ locationConfidence: 'high' });
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100">
            <MapHeader />

            <div className="flex h-[calc(100vh-84px)]">
                <div className="relative flex-1">
                    <div ref={mapContainerRef} className="absolute inset-0" />
                    <MapOverlays
                        mapReady={mapReady}
                        loading={loading}
                        error={error}
                        pinCount={pins.length}
                    />
                </div>

                <div className="w-[380px] border-l border-slate-800 bg-slate-950 p-4 overflow-y-auto space-y-6">
                    <FilterPanel
                        minOpeningBid={minOpeningBid}
                        maxOpeningBid={maxOpeningBid}
                        auctionWithinDays={auctionWithinDays}
                        locationConfidence={locationConfidence}
                        hasComps={hasComps}
                        onMinOpeningBidChange={setMinOpeningBid}
                        onMaxOpeningBidChange={setMaxOpeningBid}
                        onAuctionWithinDaysChange={setAuctionWithinDays}
                        onLocationConfidenceChange={setLocationConfidence}
                        onHasCompsChange={setHasComps}
                        onApply={applyFilters}
                        onReset={resetFilters}
                    />
                    <DetailPanel selectedPin={selectedPin} streetViewUrl={streetViewUrl ?? null} />
                    <PinList
                        pins={pins}
                        selectedPinId={selectedPin?.propertyId}
                        onSelect={pin => setSelectedPin(pin)}
                    />
                </div>
            </div>
        </div>
    );
};

export default RealSignalMapPage;
